import { useState, useEffect, useCallback, Fragment } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import TabBar from '../components/ui/TabBar';
import KpiGrid from '../components/ui/KpiGrid';
import FilterBar from '../components/ui/FilterBar';
import ActionButton from '../components/ui/ActionButton';
import { useEntities } from '../hooks/useEntities';

const API = `${API_V1}/gl`;

/* ---- interfaces ---- */
interface DashboardData {
  total_jes: number;
  provisional: number;
  manual_jes: number;
  templates: number;
  debit_volume: number;
  recent_entries: JournalEntry[];
}
interface JournalEntry {
  id: number;
  je_number: string;
  posting_date: string;
  description: string;
  je_type: string;
  je_status: string;
  total_amount: number;
  entity_code: string;
  currency: string;
  lines?: JELine[];
}
interface JELine {
  id?: number;
  account_code: string;
  description: string;
  debit: number;
  credit: number;
}
interface InquiryResult {
  account_code: string;
  account_name: string;
  opening_balance: number;
  total_debits: number;
  total_credits: number;
  closing_balance: number;
  transactions: InquiryTxn[];
}
interface InquiryTxn {
  date: string;
  je_number: string;
  description: string;
  debit: number;
  credit: number;
  running_balance: number;
}
interface ReconResult {
  account_code: string;
  account_name: string;
  gl_balance: number;
  subledger_balance: number;
  difference: number;
  unreconciled_items: ReconItem[];
}
interface ReconItem {
  id: number;
  date: string;
  description: string;
  amount: number;
  source: string;
}
interface FluxRow {
  account_code: string;
  account_name: string;
  current_balance: number;
  prior_balance: number;
  variance_amount: number;
  variance_pct: number;
  material: boolean;
}

type Tab = 'dashboard' | 'journal-entries' | 'inquiry' | 'reconciliation' | 'flux';

export default function GLWorkbench() {
  const entities = useEntities();
  const [tab, setTab] = useState<Tab>('dashboard');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  /* ---- shared filter state ---- */
  const [entityFilter, setEntityFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');

  /* ---- Dashboard ---- */
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [dashLoading, setDashLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const res = await authFetch(`${API}/dashboard`);
      if (res.ok) setDashboard(await res.json());
    } catch (e) { console.error(e); }
    setDashLoading(false);
  }, []);

  /* ---- Journal Entries ---- */
  const [jes, setJEs] = useState<JournalEntry[]>([]);
  const [jeTotal, setJETotal] = useState(0);
  const [jePage, setJEPage] = useState(0);
  const [jeLoading, setJELoading] = useState(false);
  // Lines drilldown (consolidated from the deleted Financial Reports
  // -> Posted JEs duplicate, 2026-05-15).
  const [expandedJEId, setExpandedJEId] = useState<number | null>(null);
  const [expandedLines, setExpandedLines] = useState<JELine[]>([]);
  const [loadingLines, setLoadingLines] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [jeTypeFilter, setJETypeFilter] = useState('');
  const [jeStatusFilter, setJEStatusFilter] = useState('');
  const [showCreateJE, setShowCreateJE] = useState(false);
  const [jeForm, setJEForm] = useState({
    entity_code: '',
    posting_date: new Date().toISOString().slice(0, 10),
    description: '',
    currency: 'USD',
    lines: [{ account_code: '', description: '', debit: '', credit: '' }] as { account_code: string; description: string; debit: string; credit: string }[],
  });
  const PAGE_SIZE = 50;

  const loadJEs = useCallback(async () => {
    setJELoading(true);
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(jePage * PAGE_SIZE) });
    if (entityFilter) p.set('entity_code', entityFilter);
    if (periodFilter) p.set('period', periodFilter);
    if (jeTypeFilter) p.set('je_type', jeTypeFilter);
    if (jeStatusFilter) p.set('je_status', jeStatusFilter);
    try {
      const res = await authFetch(`${API}/journal-entries?${p}`);
      if (res.ok) { const d = await res.json(); setJEs(d.journal_entries || d.items || d.entries || []); setJETotal(d.total || 0); }
    } catch (e) { console.error(e); }
    setJELoading(false);
  }, [jePage, entityFilter, periodFilter, jeTypeFilter, jeStatusFilter]);

  /* ---- Inquiry ---- */
  const [inquiryAccount, setInquiryAccount] = useState('');
  const [inquiryResult, setInquiryResult] = useState<InquiryResult | null>(null);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountSuggestions, setAccountSuggestions] = useState<{ account_code: string; account_name: string }[]>([]);

  const searchAccounts = useCallback(async (q: string) => {
    if (q.length < 2) { setAccountSuggestions([]); return; }
    try {
      const res = await authFetch(`${API}/search?q=${encodeURIComponent(q)}`);
      if (res.ok) { const d = await res.json(); setAccountSuggestions(d.accounts || d.items || []); }
    } catch { setAccountSuggestions([]); }
  }, []);

  const loadInquiry = useCallback(async (acct: string) => {
    if (!acct) return;
    setInquiryLoading(true);
    const p = new URLSearchParams();
    if (entityFilter) p.set('entity_code', entityFilter);
    if (periodFilter) p.set('period', periodFilter);
    try {
      const res = await authFetch(`${API}/inquiry/${encodeURIComponent(acct)}?${p}`);
      if (res.ok) setInquiryResult(await res.json());
      else { const e = await res.json(); showToast(e.detail || 'Account not found', false); setInquiryResult(null); }
    } catch { showToast('Inquiry failed', false); }
    setInquiryLoading(false);
  }, [entityFilter, periodFilter]);

  /* ---- Reconciliation ---- */
  const [reconAccount, setReconAccount] = useState('');
  const [reconResult, setReconResult] = useState<ReconResult | null>(null);
  const [reconLoading, setReconLoading] = useState(false);

  const loadRecon = useCallback(async (acct: string) => {
    if (!acct) return;
    setReconLoading(true);
    const p = new URLSearchParams();
    if (entityFilter) p.set('entity_code', entityFilter);
    if (periodFilter) p.set('period', periodFilter);
    try {
      const res = await authFetch(`${API}/reconciliation/${encodeURIComponent(acct)}?${p}`);
      if (res.ok) setReconResult(await res.json());
      else setReconResult(null);
    } catch { setReconResult(null); }
    setReconLoading(false);
  }, [entityFilter, periodFilter]);

  /* ---- Flux Analysis ---- */
  const [fluxRows, setFluxRows] = useState<FluxRow[]>([]);
  const [fluxLoading, setFluxLoading] = useState(false);
  const [comparisonPeriod, setComparisonPeriod] = useState('');

  const loadFlux = useCallback(async () => {
    setFluxLoading(true);
    const p = new URLSearchParams();
    if (entityFilter) p.set('entity_code', entityFilter);
    if (periodFilter) p.set('period', periodFilter);
    if (comparisonPeriod) p.set('comparison_period', comparisonPeriod);
    try {
      const res = await authFetch(`${API}/flux-analysis?${p}`);
      if (res.ok) { const d = await res.json(); setFluxRows(d.rows || d.items || []); }
    } catch (e) { console.error(e); }
    setFluxLoading(false);
  }, [entityFilter, periodFilter, comparisonPeriod]);

  /* ---- Tab change loader ---- */
  useEffect(() => {
    if (tab === 'dashboard') loadDashboard();
    else if (tab === 'journal-entries') loadJEs();
    else if (tab === 'flux') loadFlux();
  }, [tab, loadDashboard, loadJEs, loadFlux]);

  /* ---- JE actions ---- */
  const handleCreateJE = async () => {
    const totalDebits = jeForm.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
    const totalCredits = jeForm.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    if (Math.abs(totalDebits - totalCredits) > 0.01) { showToast('Journal entry must be balanced (debits = credits)', false); return; }
    if (!jeForm.entity_code) { showToast('Select an entity', false); return; }
    try {
      const payload = {
        ...jeForm,
        lines: jeForm.lines.filter(l => l.account_code).map(l => ({
          account_code: l.account_code,
          description: l.description,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
        })),
      };
      const res = await authFetch(`${API}/journal-entries`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) { showToast('Journal entry created', true); setShowCreateJE(false); resetJEForm(); loadJEs(); }
      else { const e = await res.json(); showToast(e.detail || 'Error creating JE', false); }
    } catch { showToast('Failed to create JE', false); }
  };

  const handleJEAction = async (id: number, action: 'confirm' | 'reverse') => {
    try {
      const res = await authFetch(`${API}/journal-entries/${id}/${action}`, { method: 'POST' });
      if (res.ok) { showToast(`JE ${action}ed`, true); loadJEs(); }
      else { const e = await res.json(); showToast(e.detail || `Failed to ${action}`, false); }
    } catch { showToast(`${action} failed`, false); }
  };

  /* ---- Lines drilldown (consolidated from old Posted JEs view) ---- */
  const toggleLines = async (jeId: number) => {
    if (expandedJEId === jeId) { setExpandedJEId(null); setExpandedLines([]); return; }
    setExpandedJEId(jeId); setLoadingLines(true); setExpandedLines([]);
    try {
      const res = await authFetch(`${API}/journal-entries/${jeId}`);
      if (res.ok) {
        const d = await res.json();
        setExpandedLines(d.lines || []);
      } else {
        showToast('Failed to load lines', false);
      }
    } catch { showToast('Failed to load lines', false); }
    setLoadingLines(false);
  };

  /* ---- Export All Confirmed (carried over from deleted Posted JEs view) ---- */
  const handleExportAllConfirmed = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ status: 'CONFIRMED' });
      if (entityFilter) params.set('entity', entityFilter);
      if (periodFilter) {
        const [y, m] = periodFilter.split('-');
        if (y) params.set('period_year', y);
        if (m) params.set('period_month', m);
      }
      const res = await authFetch(`${API_V1}/financial/export/je-package/bulk?${params}`);
      if (!res.ok) { showToast('Export failed', false); setExporting(false); return; }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `AW_JE_Package_Bulk.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      showToast('Export started', true);
    } catch { showToast('Export failed', false); }
    setExporting(false);
  };

  const resetJEForm = () => setJEForm({ entity_code: '', posting_date: new Date().toISOString().slice(0, 10), description: '', currency: 'USD', lines: [{ account_code: '', description: '', debit: '', credit: '' }] });
  const addJELine = () => setJEForm({ ...jeForm, lines: [...jeForm.lines, { account_code: '', description: '', debit: '', credit: '' }] });
  const updateJELine = (i: number, field: string, value: string) => { const lines = [...jeForm.lines]; (lines[i] as any)[field] = value; setJEForm({ ...jeForm, lines }); };
  const removeJELine = (i: number) => { if (jeForm.lines.length <= 1) return; const lines = jeForm.lines.filter((_, idx) => idx !== i); setJEForm({ ...jeForm, lines }); };

  /* ---- JE form balance calc ---- */
  const totalDebits = jeForm.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredits = jeForm.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01;

  /* ---- styles ---- */
  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-subtle)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const entityOpts = entities.map(e => ({ value: e.entity_code, label: e.entity_code }));

  return (
    <PageLayout
      title="GL Workbench"
      subtitle="Journal entries, account inquiry, reconciliation, and flux analysis"
      actions={tab === 'journal-entries' ? [
        { label: exporting ? 'Exporting...' : 'Export All Confirmed', onClick: handleExportAllConfirmed, variant: 'secondary' as const },
        { label: '+ Create JE', onClick: () => { resetJEForm(); setShowCreateJE(true); } },
      ] : undefined}
    >
      <TabBar
        tabs={[
          { key: 'dashboard', label: 'Dashboard' },
          { key: 'journal-entries', label: 'Journal Entries' },
          { key: 'inquiry', label: 'GL Inquiry' },
          { key: 'reconciliation', label: 'Reconciliation' },
          { key: 'flux', label: 'Flux Analysis' },
        ]}
        active={tab}
        onChange={k => setTab(k as Tab)}
      />

      {/* ================================================================== */}
      {/* TAB 1: Dashboard                                                    */}
      {/* ================================================================== */}
      {tab === 'dashboard' && (
        <>
          {dashLoading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-hint)' }}>Loading...</div> : dashboard ? (
            <>
              <KpiGrid columns={5} items={[
                { label: 'Total JEs', value: dashboard.total_jes, color: 'var(--text-primary)' },
                { label: 'Provisional', value: dashboard.provisional, color: 'var(--warning)' },
                { label: 'Manual JEs', value: dashboard.manual_jes, color: 'var(--accent)' },
                { label: 'Templates', value: dashboard.templates, color: 'var(--text-secondary)' },
                { label: 'Debit Volume', value: formatAmountCompact(dashboard.debit_volume), color: 'var(--positive)' },
              ]} />

              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Recent Journal Entries</div>
              {dashboard.recent_entries && dashboard.recent_entries.length > 0 ? (
                <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      {['JE #', 'Date', 'Description', 'Type', 'Status', 'Amount'].map(h => (
                        <th key={h} style={{ ...thStyle, textAlign: h === 'Amount' ? 'right' : 'left' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>{dashboard.recent_entries.slice(0, 10).map((je, i) => (
                      <tr key={je.id || i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--accent)' }}>{je.je_number}</td>
                        <td style={tdStyle}>{je.posting_date}</td>
                        <td style={tdStyle}>{je.description || '-'}</td>
                        <td style={tdStyle}><StatusBadge status={je.je_type || 'MANUAL'} /></td>
                        <td style={tdStyle}><StatusBadge status={je.je_status || 'CONFIRMED'} /></td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(je.total_amount)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-hint)' }}>No recent entries.</div>}
            </>
          ) : <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-hint)' }}>No dashboard data available.</div>}
        </>
      )}

      {/* ================================================================== */}
      {/* TAB 2: Journal Entries                                              */}
      {/* ================================================================== */}
      {tab === 'journal-entries' && (
        <>
          <FilterBar filters={[
            { key: 'entity', label: 'Entity', type: 'select', value: entityFilter, onChange: v => { setEntityFilter(v); setJEPage(0); }, options: entityOpts, width: 180 },
            { key: 'period', label: 'Period', type: 'search', value: periodFilter, onChange: v => { setPeriodFilter(v); setJEPage(0); }, placeholder: 'Period (e.g. 2026-03)', width: 160 },
            { key: 'type', label: 'Type', type: 'select', value: jeTypeFilter, onChange: v => { setJETypeFilter(v); setJEPage(0); }, options: ['MANUAL', 'ADJUSTMENT', 'CONVERSION', 'REVERSAL', 'IMPORT', 'TEMPLATE'].map(s => ({ value: s, label: s })), width: 160 },
            { key: 'status', label: 'Status', type: 'select', value: jeStatusFilter, onChange: v => { setJEStatusFilter(v); setJEPage(0); }, options: ['PROVISIONAL', 'CONFIRMED', 'REVERSED', 'DRAFT'].map(s => ({ value: s, label: s })), width: 160 },
          ]} />

          {jeLoading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-hint)' }}>Loading...</div> : jes.length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-hint)' }}>No journal entries found.</div> : (
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  {['JE #', 'Date', 'Entity', 'Description', 'Type', 'Status', 'Amount', 'Actions'].map(h => (
                    <th key={h} style={{ ...thStyle, textAlign: h === 'Amount' ? 'right' : 'left' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{jes.map(je => (
                  <Fragment key={je.id}>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: expandedJEId === je.id ? 'var(--accent-subtle)' : 'transparent' }}>
                    <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--accent)' }}>{je.je_number}</td>
                    <td style={tdStyle}>{je.posting_date}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{je.entity_code}</td>
                    <td style={tdStyle}>{je.description || '-'}</td>
                    <td style={tdStyle}><StatusBadge status={je.je_type || 'MANUAL'} /></td>
                    <td style={tdStyle}><StatusBadge status={je.je_status || 'CONFIRMED'} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(je.total_amount)}</td>
                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <ActionButton label={expandedJEId === je.id ? 'Hide' : 'Lines'} onClick={() => toggleLines(je.id)} variant="secondary" size="sm" />
                        {je.je_status === 'PROVISIONAL' && <ActionButton label="Confirm" onClick={() => handleJEAction(je.id, 'confirm')} variant="success" size="sm" />}
                        {je.je_status === 'CONFIRMED' && <ActionButton label="Reverse" onClick={() => handleJEAction(je.id, 'reverse')} variant="danger" size="sm" />}
                      </div>
                    </td>
                  </tr>
                  {expandedJEId === je.id && (
                    <tr style={{ background: 'var(--accent-subtle)' }}>
                      <td colSpan={8} style={{ padding: '8px 24px 16px' }}>
                        {loadingLines ? (
                          <div style={{ fontSize: 11, color: 'var(--text-hint)' }}>Loading lines...</div>
                        ) : expandedLines.length === 0 ? (
                          <div style={{ fontSize: 11, color: 'var(--text-hint)' }}>No lines on this JE.</div>
                        ) : (
                          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                            <thead><tr>
                              {['Account', 'Name', 'Description', 'Debit', 'Credit'].map(h => (
                                <th key={h} style={{ padding: 6, textAlign: ['Debit', 'Credit'].includes(h) ? 'right' : 'left', fontSize: 10, color: 'var(--text-hint)', textTransform: 'uppercase' }}>{h}</th>
                              ))}
                            </tr></thead>
                            <tbody>
                              {expandedLines.map((l: any, idx: number) => {
                                // Backend returns debit_amount / credit_amount (gl-workbench API).
                                // Other JE endpoints (financial/export) use debit / credit.
                                // Tolerate both shapes so the view doesn't silently render '-'.
                                const dr = Number(l.debit_amount ?? l.debit ?? 0);
                                const cr = Number(l.credit_amount ?? l.credit ?? 0);
                                return (
                                <tr key={idx} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                  <td style={{ padding: 6, fontFamily: 'monospace' }}>{l.account_code}</td>
                                  <td style={{ padding: 6, color: 'var(--text-secondary)' }}>{l.account_name || '-'}</td>
                                  <td style={{ padding: 6, color: 'var(--text-secondary)' }}>{l.description || '-'}</td>
                                  <td style={{ padding: 6, textAlign: 'right', fontWeight: 600, color: dr > 0 ? 'var(--positive)' : 'var(--text-hint)' }}>{dr > 0 ? formatAmount(dr) : '-'}</td>
                                  <td style={{ padding: 6, textAlign: 'right', fontWeight: 600, color: cr > 0 ? 'var(--negative)' : 'var(--text-hint)' }}>{cr > 0 ? formatAmount(cr) : '-'}</td>
                                </tr>
                                );
                              })}
                              <tr style={{ borderTop: '2px solid var(--border-default)', fontWeight: 700 }}>
                                <td colSpan={3} style={{ padding: 6, textAlign: 'right' }}>Total</td>
                                <td style={{ padding: 6, textAlign: 'right' }}>{formatAmount(expandedLines.reduce((s: number, l: any) => s + Number(l.debit_amount ?? l.debit ?? 0), 0))}</td>
                                <td style={{ padding: 6, textAlign: 'right' }}>{formatAmount(expandedLines.reduce((s: number, l: any) => s + Number(l.credit_amount ?? l.credit ?? 0), 0))}</td>
                              </tr>
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}</tbody>
              </table>
            </div>
          )}

          {jeTotal > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
              <ActionButton label="PREV" onClick={() => setJEPage(Math.max(0, jePage - 1))} variant="ghost" size="sm" disabled={jePage === 0} />
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{jePage * PAGE_SIZE + 1}-{Math.min((jePage + 1) * PAGE_SIZE, jeTotal)} of {jeTotal}</span>
              <ActionButton label="NEXT" onClick={() => setJEPage(jePage + 1)} variant="ghost" size="sm" disabled={(jePage + 1) * PAGE_SIZE >= jeTotal} />
            </div>
          )}
        </>
      )}

      {/* ================================================================== */}
      {/* TAB 3: GL Inquiry                                                   */}
      {/* ================================================================== */}
      {tab === 'inquiry' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
              <input placeholder="Search account code or name..." value={accountSearch} onChange={e => { setAccountSearch(e.target.value); searchAccounts(e.target.value); }} style={inputStyle} />
              {accountSuggestions.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 6, maxHeight: 200, overflow: 'auto', marginTop: 2 }}>
                  {accountSuggestions.map(a => (
                    <div key={a.account_code} onClick={() => { setInquiryAccount(a.account_code); setAccountSearch(a.account_code); setAccountSuggestions([]); loadInquiry(a.account_code); }} style={{ padding: '8px 12px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--border-subtle)' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                      <span style={{ fontWeight: 600 }}>{a.account_code}</span> <span style={{ color: 'var(--text-secondary)' }}>- {a.account_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <FilterBar filters={[
              { key: 'entity', label: 'Entity', type: 'select', value: entityFilter, onChange: setEntityFilter, options: entityOpts, width: 180 },
              { key: 'period', label: 'Period', type: 'search', value: periodFilter, onChange: setPeriodFilter, placeholder: 'Period (e.g. 2026-03)', width: 160 },
            ]} />
            <ActionButton label="Search" onClick={() => loadInquiry(inquiryAccount)} />
          </div>

          {inquiryLoading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-hint)' }}>Loading...</div> : inquiryResult ? (
            <>
              <KpiGrid columns={5} items={[
                { label: 'Account', value: `${inquiryResult.account_code} - ${inquiryResult.account_name}`, color: 'var(--accent)' },
                { label: 'Opening Balance', value: formatAmountCompact(inquiryResult.opening_balance), color: 'var(--text-primary)' },
                { label: 'Debits', value: formatAmountCompact(inquiryResult.total_debits), color: 'var(--positive)' },
                { label: 'Credits', value: formatAmountCompact(inquiryResult.total_credits), color: 'var(--negative)' },
                { label: 'Closing Balance', value: formatAmountCompact(inquiryResult.closing_balance), color: 'var(--accent)' },
              ]} />

              {inquiryResult.transactions && inquiryResult.transactions.length > 0 ? (
                <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      {['Date', 'JE #', 'Description', 'Debit', 'Credit', 'Balance'].map(h => (
                        <th key={h} style={{ ...thStyle, textAlign: ['Debit', 'Credit', 'Balance'].includes(h) ? 'right' : 'left' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>{inquiryResult.transactions.map((txn, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={tdStyle}>{txn.date}</td>
                        <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--accent)' }}>{txn.je_number}</td>
                        <td style={tdStyle}>{txn.description || '-'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: txn.debit > 0 ? 'var(--positive)' : 'var(--text-secondary)' }}>{txn.debit > 0 ? formatAmount(txn.debit) : '-'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: txn.credit > 0 ? 'var(--negative)' : 'var(--text-secondary)' }}>{txn.credit > 0 ? formatAmount(txn.credit) : '-'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(txn.running_balance)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-hint)' }}>No transactions for this account/period.</div>}
            </>
          ) : <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-hint)' }}>Enter an account code and click Search to view account activity.</div>}
        </>
      )}

      {/* ================================================================== */}
      {/* TAB 4: Reconciliation                                               */}
      {/* ================================================================== */}
      {tab === 'reconciliation' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input placeholder="Account code" value={reconAccount} onChange={e => setReconAccount(e.target.value)} style={{ ...inputStyle, maxWidth: 200 }} />
            <FilterBar filters={[
              { key: 'entity', label: 'Entity', type: 'select', value: entityFilter, onChange: setEntityFilter, options: entityOpts, width: 180 },
              { key: 'period', label: 'Period', type: 'search', value: periodFilter, onChange: setPeriodFilter, placeholder: 'Period (e.g. 2026-03)', width: 160 },
            ]} />
            <ActionButton label="Reconcile" onClick={() => loadRecon(reconAccount)} />
          </div>

          {reconLoading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-hint)' }}>Loading...</div> : reconResult ? (
            <>
              <KpiGrid columns={3} items={[
                { label: 'GL Balance', value: formatAmountCompact(reconResult.gl_balance), color: 'var(--accent)' },
                { label: 'Subledger Balance', value: formatAmountCompact(reconResult.subledger_balance), color: 'var(--accent)' },
                { label: 'Difference', value: formatAmountCompact(reconResult.difference), color: Math.abs(reconResult.difference) < 0.01 ? 'var(--positive)' : 'var(--negative)' },
              ]} />

              {reconResult.unreconciled_items && reconResult.unreconciled_items.length > 0 ? (
                <>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Unreconciled Items</div>
                  <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr>
                        {['Date', 'Description', 'Amount', 'Source'].map(h => (
                          <th key={h} style={{ ...thStyle, textAlign: h === 'Amount' ? 'right' : 'left' }}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>{reconResult.unreconciled_items.map(item => (
                        <tr key={item.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          <td style={tdStyle}>{item.date}</td>
                          <td style={tdStyle}>{item.description}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: item.amount >= 0 ? 'var(--positive)' : 'var(--negative)' }}>{formatAmount(item.amount)}</td>
                          <td style={{ ...tdStyle, fontSize: 10, color: 'var(--text-secondary)' }}>{item.source}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </>
              ) : <div style={{ textAlign: 'center', padding: 30, color: 'var(--positive)', fontWeight: 600 }}>Fully reconciled -- no outstanding items.</div>}
            </>
          ) : <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-hint)' }}>Enter an account code and click Reconcile to view GL vs subledger comparison.</div>}
        </>
      )}

      {/* ================================================================== */}
      {/* TAB 5: Flux Analysis                                                */}
      {/* ================================================================== */}
      {tab === 'flux' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <FilterBar filters={[
              { key: 'entity', label: 'Entity', type: 'select', value: entityFilter, onChange: setEntityFilter, options: entityOpts, width: 180 },
              { key: 'period', label: 'Period', type: 'search', value: periodFilter, onChange: setPeriodFilter, placeholder: 'Current period (e.g. 2026-03)', width: 180 },
              { key: 'comparison', label: 'Comparison', type: 'search', value: comparisonPeriod, onChange: setComparisonPeriod, placeholder: 'Comparison period (e.g. 2026-02)', width: 180 },
            ]} />
            <ActionButton label="Analyze" onClick={loadFlux} />
          </div>

          {fluxLoading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-hint)' }}>Loading...</div> : fluxRows.length > 0 ? (
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  {['Account', 'Account Name', 'Current Balance', 'Prior Balance', 'Variance $', 'Variance %', 'Material'].map(h => (
                    <th key={h} style={{ ...thStyle, textAlign: ['Current Balance', 'Prior Balance', 'Variance $', 'Variance %'].includes(h) ? 'right' : 'left' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{fluxRows.map((row, i) => {
                  const favorable = row.variance_amount >= 0;
                  const varColor = favorable ? 'var(--positive)' : 'var(--negative)';
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)', background: row.material ? 'rgba(239,68,68,0.04)' : 'transparent' }}>
                      <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--accent)' }}>{row.account_code}</td>
                      <td style={tdStyle}>{row.account_name}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(row.current_balance)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(row.prior_balance)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: varColor }}>{formatAmount(row.variance_amount)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: varColor }}>{row.variance_pct != null ? `${row.variance_pct.toFixed(1)}%` : '-'}</td>
                      <td style={tdStyle}>{row.material ? <span style={{ padding: '2px 8px', borderRadius: 4, background: 'var(--negative-bg)', color: 'var(--negative)', fontSize: 10, fontWeight: 700 }}>MATERIAL</span> : <span style={{ color: 'var(--text-hint)', fontSize: 10 }}>--</span>}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          ) : <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-hint)' }}>Select entity and periods, then click Analyze to view flux analysis.</div>}
        </>
      )}

      {/* ================================================================== */}
      {/* Create JE Modal                                                     */}
      {/* ================================================================== */}
      <Modal open={showCreateJE} onClose={() => setShowCreateJE(false)} title="Create Journal Entry" size="lg" footer={
        <>
          <ActionButton label="Cancel" onClick={() => setShowCreateJE(false)} variant="secondary" />
          <ActionButton label="Create JE" onClick={handleCreateJE} disabled={!isBalanced || totalDebits === 0} />
        </>
      }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <select value={jeForm.entity_code} onChange={e => setJEForm({ ...jeForm, entity_code: e.target.value })} style={inputStyle}>
            <option value="">Select Entity *</option>
            {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} -- {e.entity_name} ({e.functional_currency})</option>)}
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="date" value={jeForm.posting_date} onChange={e => setJEForm({ ...jeForm, posting_date: e.target.value })} style={inputStyle} />
            <input placeholder="Currency" value={jeForm.currency} onChange={e => setJEForm({ ...jeForm, currency: e.target.value })} style={{ ...inputStyle, maxWidth: 100 }} />
          </div>
          <input placeholder="Description" value={jeForm.description} onChange={e => setJEForm({ ...jeForm, description: e.target.value })} style={inputStyle} />

          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>LINES</div>
              <div style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 6, background: isBalanced ? 'var(--positive-bg)' : 'var(--negative-bg)', color: isBalanced ? 'var(--positive)' : 'var(--negative)' }}>
                {isBalanced ? 'BALANCED' : `OUT OF BALANCE: ${formatAmount(Math.abs(totalDebits - totalCredits))}`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              <div style={{ flex: 2, fontSize: 9, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', padding: '0 4px' }}>Account</div>
              <div style={{ flex: 3, fontSize: 9, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', padding: '0 4px' }}>Description</div>
              <div style={{ flex: 1, fontSize: 9, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', padding: '0 4px' }}>Debit</div>
              <div style={{ flex: 1, fontSize: 9, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', padding: '0 4px' }}>Credit</div>
              <div style={{ width: 24 }}></div>
            </div>
            {jeForm.lines.map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input placeholder="Account code" value={l.account_code} onChange={e => updateJELine(i, 'account_code', e.target.value)} style={{ ...inputStyle, flex: 2 }} />
                <input placeholder="Description" value={l.description} onChange={e => updateJELine(i, 'description', e.target.value)} style={{ ...inputStyle, flex: 3 }} />
                <input placeholder="0.00" type="number" step="0.01" value={l.debit} onChange={e => updateJELine(i, 'debit', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                <input placeholder="0.00" type="number" step="0.01" value={l.credit} onChange={e => updateJELine(i, 'credit', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                <button onClick={() => removeJELine(i)} style={{ width: 24, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', color: 'var(--text-hint)', cursor: jeForm.lines.length > 1 ? 'pointer' : 'not-allowed', fontSize: 16, opacity: jeForm.lines.length > 1 ? 1 : 0.3 }} title="Remove line">x</button>
              </div>
            ))}
            <button onClick={addJELine} style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>+ Add Line</button>

            <div style={{ display: 'flex', gap: 6, marginTop: 8, borderTop: '1px solid var(--border-subtle)', paddingTop: 8 }}>
              <div style={{ flex: 2 }}></div>
              <div style={{ flex: 3, textAlign: 'right', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>Totals:</div>
              <div style={{ flex: 1, fontSize: 12, fontWeight: 700, color: 'var(--positive)', textAlign: 'right', paddingRight: 12 }}>{formatAmount(totalDebits)}</div>
              <div style={{ flex: 1, fontSize: 12, fontWeight: 700, color: 'var(--negative)', textAlign: 'right', paddingRight: 12 }}>{formatAmount(totalCredits)}</div>
              <div style={{ width: 24 }}></div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Toast */}
      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 14px', fontSize: 11, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.04em',
  color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)',
};
